import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getJulietCandidates, getCandidateDetail } from '../services/api';
import CandidateProfileModal from './CandidateProfileModal';
import './Juliet.css';

/**
 * Generic styled dropdown filter.
 *
 * options  – array of { value, label } objects (plain strings also accepted)
 * placeholder – label shown when value === 'all'
 * searchable  – show a search box (useful for long lists)
 */
function FilterDropdown({ value, options, onChange, placeholder = 'All', searchable = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  // Normalise options to { value, label }
  const normalised = options.map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  );

  const filtered = searchable
    ? normalised.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : normalised;

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
    setSearch('');
  };

  const activeLabel =
    value === 'all'
      ? placeholder
      : (normalised.find((o) => o.value === value)?.label ?? value);

  const isActive = value !== 'all';

  const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path d="M20 6L9 17l-5-5" stroke="#8b5cf6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="pa-dropdown" ref={wrapperRef}>
      <button
        type="button"
        className={`pa-dropdown-trigger${isOpen ? ' open' : ''}${isActive ? ' has-value' : ''}`}
        onClick={() => setIsOpen((o) => !o)}
      >
        <span className="pa-dropdown-label">{activeLabel}</span>
        <span className="pa-dropdown-icons">
          {isActive && (
            <span
              className="pa-dropdown-clear"
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); handleSelect('all'); }}
              onKeyDown={(e) => e.key === 'Enter' && (e.stopPropagation(), handleSelect('all'))}
              title="Clear filter"
            >
              ✕
            </span>
          )}
          <svg className={`pa-dropdown-chevron${isOpen ? ' rotated' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="pa-dropdown-panel">
          {searchable && (
            <div className="pa-dropdown-search-row">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="pa-search-icon">
                <circle cx="11" cy="11" r="8" stroke="#9ca3af" strokeWidth="2"/>
                <path d="M21 21l-4.35-4.35" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <input
                className="pa-dropdown-search-input"
                type="text"
                placeholder={`Search ${placeholder.toLowerCase()}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              {search && (
                <button type="button" className="pa-search-clear" onClick={() => setSearch('')}>✕</button>
              )}
            </div>
          )}

          <div className="pa-dropdown-list">
            <button
              type="button"
              className={`pa-dropdown-item${!isActive ? ' selected' : ''}`}
              onClick={() => handleSelect('all')}
            >
              <span>{placeholder}</span>
              {!isActive && <CheckIcon />}
            </button>

            {filtered.length === 0 ? (
              <div className="pa-dropdown-empty">No matches for "{search}"</div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`pa-dropdown-item${value === opt.value ? ' selected' : ''}`}
                  onClick={() => handleSelect(opt.value)}
                >
                  <span>{opt.label}</span>
                  {value === opt.value && <CheckIcon />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Juliet() {
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    embedding_ready: 0,
    parsing: 0,
    failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [parseFilter, setParseFilter] = useState('all');
  const [practiceAreaFilter, setPracticeAreaFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  // Tracks the fixed-position overflow tooltip (escapes overflow:auto clipping)
  const [overflowTooltip, setOverflowTooltip] = useState(null);
  const tooltipHideTimer = useRef(null);

  const showTooltip = (e, items) => {
    clearTimeout(tooltipHideTimer.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setOverflowTooltip({ x: rect.left + rect.width / 2, y: rect.top, items });
  };
  const hideTooltip = () => {
    tooltipHideTimer.current = setTimeout(() => setOverflowTooltip(null), 120);
  };
  const keepTooltip = () => clearTimeout(tooltipHideTimer.current);
  const allCandidatesCheckboxRef = useRef(null);
  // Accumulates all practice areas ever seen so the dropdown stays populated
  // even while a filter is active.
  const [practiceAreasPool, setPracticeAreasPool] = useState([]);
  const practiceAreasSetRef = useRef(new Set());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getJulietCandidates({
        page: currentPage,
        page_size: 10,
        search: searchTerm || undefined,
        parse_status: parseFilter !== 'all' ? parseFilter : undefined,
        practice_areas: practiceAreaFilter !== 'all' ? practiceAreaFilter : undefined,
      });
      const results = data.results || [];

      // Accumulate practice areas across all fetches (never shrink the pool)
      let poolChanged = false;
      results.forEach((c) => {
        if (Array.isArray(c.practice_areas)) {
          c.practice_areas.forEach((pa) => {
            if (pa && !practiceAreasSetRef.current.has(pa)) {
              practiceAreasSetRef.current.add(pa);
              poolChanged = true;
            }
          });
        }
      });
      if (poolChanged) {
        setPracticeAreasPool(Array.from(practiceAreasSetRef.current).sort((a, b) => a.localeCompare(b)));
      }

      setCandidates(results);
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / 10));
      if (data.stats) {
        setStats(data.stats);
      }
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, parseFilter, practiceAreaFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Clear selections when filters, search, or page change
  useEffect(() => {
    setSelectedCandidateIds([]);
  }, [currentPage, searchTerm, parseFilter, practiceAreaFilter]);

  const handleCandidateClick = async (candidateId) => {
    try {
      setSelectedCandidateId(candidateId);
      setShowModal(true);
      // Fetch full candidate detail for modal
      const candidateDetail = await getCandidateDetail(candidateId);
      setSelectedCandidate(candidateDetail);
    } catch (err) {
      setError(`Failed to load candidate details: ${err.message}`);
      setShowModal(false);
      setSelectedCandidateId(null);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCandidate(null);
    setSelectedCandidateId(null);
  };

  const toggleCandidateSelection = (candidateId, checked) => {
    setSelectedCandidateIds((prev) => {
      if (checked) {
        if (prev.includes(candidateId)) return prev;
        return [...prev, candidateId];
      }
      return prev.filter((id) => id !== candidateId);
    });
  };

  const handleToggleAllCandidates = (checked) => {
    const visibleIds = candidates.map((candidate) => candidate.id);
    if (checked) {
      setSelectedCandidateIds((prev) => {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.add(id));
        return Array.from(next);
      });
    } else {
      setSelectedCandidateIds((prev) =>
        prev.filter((id) => !visibleIds.includes(id))
      );
    }
  };

  const visibleCandidateIds = candidates.map((candidate) => candidate.id);
  const visibleSelectedCandidates = selectedCandidateIds.filter((id) =>
    visibleCandidateIds.includes(id)
  ).length;
  const allVisibleCandidatesSelected =
    candidates.length > 0 &&
    visibleSelectedCandidates === candidates.length;
  const someVisibleCandidatesSelected =
    visibleSelectedCandidates > 0 &&
    visibleSelectedCandidates < candidates.length;

  useEffect(() => {
    if (allCandidatesCheckboxRef.current) {
      allCandidatesCheckboxRef.current.indeterminate =
        someVisibleCandidatesSelected;
    }
  }, [someVisibleCandidatesSelected, allVisibleCandidatesSelected, candidates.length]);

  const getParseStatusBadge = (status) => {
    const badges = {
      complete: { label: 'Complete', icon: '✓', color: '#10b981' },
      parsing: { label: 'Parsing', icon: '⟳', color: '#f59e0b' },
      failed: { label: 'Failed', icon: '×', color: '#ef4444' },
    };
    return badges[status] || badges.parsing;
  };

  const getEmbeddingStatusBadge = (status) => {
    if (status === 'ready') {
      return { label: 'Ready', color: '#10b981' };
    } else if (status === 'pending') {
      return { label: 'Pending', color: '#9ca3af' };
    }
    return { label: 'N/A', color: '#9ca3af' };
  };

  const formatTimeAgo = (timeString) => {
    if (!timeString) return 'Never';
    return timeString; // Already formatted by backend
  };

  // practiceAreasPool is maintained via the ref in fetchData above.

  return (
    <div className="juliet-page">
      {/* Header */}
      <div className="juliet-header">
        <div className="juliet-header-left">
          <h1 className="juliet-title">
            <span className="juliet-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 7C16 9.21 14.21 11 12 11C9.79 11 8 9.21 8 7C8 4.79 9.79 3 12 3C14.21 3 16 4.79 16 7ZM12 9C13.1 9 14 8.1 14 7C14 5.9 13.1 5 12 5C10.9 5 10 5.9 10 7C10 8.1 10.9 9 12 9ZM12 13C14.67 13 20 14.33 20 17V19H4V17C4 14.33 9.33 13 12 13ZM12 11C9.33 11 4 12.33 4 15V17H20V15C20 12.33 14.67 11 12 11Z" fill="#8b5cf6"/>
              </svg>
            </span>
            Juliet - Candidate Intelligence
          </h1>
          <p className="juliet-subtitle">
            AI-powered candidate ingestion and understanding pipeline
          </p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="juliet-stats">
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">TOTAL CANDIDATES</div>
            <div className="stat-value">{stats.total}</div>
          </div>
          <div className="stat-icon stat-icon-blue">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 7C16 9.21 14.21 11 12 11C9.79 11 8 9.21 8 7C8 4.79 9.79 3 12 3C14.21 3 16 4.79 16 7ZM12 9C13.1 9 14 8.1 14 7C14 5.9 13.1 5 12 5C10.9 5 10 5.9 10 7C10 8.1 10.9 9 12 9ZM12 13C14.67 13 20 14.33 20 17V19H4V17C4 14.33 9.33 13 12 13ZM12 11C9.33 11 4 12.33 4 15V17H20V15C20 12.33 14.67 11 12 11Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">EMBEDDING READY</div>
            <div className="stat-value">{stats.embedding_ready}</div>
          </div>
          <div className="stat-icon stat-icon-green">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">PARSING</div>
            <div className="stat-value">{stats.parsing}</div>
          </div>
          <div className="stat-icon stat-icon-yellow">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 6V9L16 5L12 1V4C7.58 4 4 7.58 4 12C4 13.57 4.46 15.03 5.24 16.26L6.7 14.8C6.25 13.97 6 13.01 6 12C6 8.69 8.69 6 12 6ZM18.76 7.74L17.3 9.2C17.74 10.03 18 10.99 18 12C18 15.31 15.31 18 12 18V15L8 19L12 23V20C16.42 20 20 16.42 20 12C20 10.43 19.54 8.97 18.76 7.74Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-label">FAILED</div>
            <div className="stat-value">{stats.failed}</div>
          </div>
          <div className="stat-icon stat-icon-red">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="juliet-table-container">
        {/* Toolbar */}
        <div className="juliet-toolbar">
          <div className="toolbar-left">
            <label className="checkbox-label">
              <input
                type="checkbox"
                ref={allCandidatesCheckboxRef}
                checked={allVisibleCandidatesSelected}
                onChange={(e) => handleToggleAllCandidates(e.target.checked)}
              />
              <span>All Candidates ({totalCount})</span>
            </label>
          </div>
          <div className="toolbar-right">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="#9ca3af"/>
              </svg>
              <input
                type="text"
                placeholder="Search candidates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setCurrentPage(1);
                    fetchData();
                  }
                }}
              />
            </div>
            <FilterDropdown
              value={parseFilter}
              options={[
                { value: 'complete', label: 'Complete' },
                { value: 'failed', label: 'Failed' },
              ]}
              placeholder="All Parse Status"
              onChange={(val) => { setParseFilter(val); setCurrentPage(1); }}
            />
            <FilterDropdown
              value={practiceAreaFilter}
              options={practiceAreasPool}
              placeholder="All Practice Areas"
              searchable
              onChange={(val) => { setPracticeAreaFilter(val); setCurrentPage(1); }}
            />
          </div>
        </div>

        {/* Table */}
        <div className="juliet-table-wrapper">
          <table className="juliet-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  {/* <input type="checkbox" /> */}
                </th>
                <th>CANDIDATE NAME</th>
                <th>PRACTICE AREAS</th>
                <th>SENIORITY</th>
                <th>LOCATION</th>
                <th>PARSE STATUS</th>
                <th>EMBEDDING</th>
                <th>SYNCED</th>
              </tr>
            </thead>
            <tbody>
              {candidates.length === 0 ? (
                <tr>
                  <td colSpan="8" className="empty-state">
                    {error
                      ? `Error loading candidates: ${error}`
                      : loading
                        ? 'Loading candidates...'
                        : 'No candidates found'}
                  </td>
                </tr>
              ) : (
                candidates.map((candidate) => {
                  const candidateProfile = candidate.pearch_profile || {};
                  const parseStatus = getParseStatusBadge(candidate.parse_status);
                  const embeddingStatus = getEmbeddingStatusBadge(candidate.embedding_status);
                  
                  return (
                    <tr
                      key={candidate.id}
                      onClick={() => handleCandidateClick(candidate.id)}
                      className="table-row-clickable"
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedCandidateIds.includes(candidate.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) =>
                            toggleCandidateSelection(
                              candidate.id,
                              e.target.checked
                            )
                          }
                        />
                      </td>
                      <td>
                        <div className="candidate-name-cell">
                          <div className="candidate-avatar-wrapper">
                            <div className="candidate-avatar-placeholder">
                              {(candidateProfile.first_name?.[0] || candidateProfile.full_name?.[0] || '?').toUpperCase()}
                            </div>
                            {candidateProfile.picture_url && (
                              <img
                                src={candidateProfile.picture_url}
                                alt={candidateProfile.full_name || 'Candidate'}
                                className="candidate-avatar"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                          </div>
                          <div className="candidate-name-info">
                            <div className="candidate-name">
                              {candidateProfile.full_name || candidate.source_id}
                            </div>
                            <div className="candidate-email">
                              {candidateProfile.best_personal_email || candidateProfile.best_business_email || 'No email'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="practice-areas-cell">
                          {candidate.practice_areas && candidate.practice_areas.length > 0 ? (() => {
                            // When a filter is active, bubble the matching tag to the front
                            const sorted = practiceAreaFilter !== 'all'
                              ? [...candidate.practice_areas].sort((a) =>
                                  a.toLowerCase() === practiceAreaFilter.toLowerCase() ? -1 : 0
                                )
                              : candidate.practice_areas;
                            const visible = sorted.slice(0, 3);
                            const overflow = sorted.length - visible.length;
                            return (
                              <>
                                {visible.map((pa, idx) => (
                                  <span
                                    key={idx}
                                    className={`practice-area-tag${practiceAreaFilter !== 'all' && pa.toLowerCase() === practiceAreaFilter.toLowerCase() ? ' matched' : ''}`}
                                  >
                                    {pa}
                                  </span>
                                ))}
                                {overflow > 0 && (
                                  <span
                                    className="practice-area-overflow"
                                    onMouseEnter={(e) => showTooltip(e, sorted.slice(3))}
                                    onMouseLeave={hideTooltip}
                                  >
                                    +{overflow}
                                  </span>
                                )}
                              </>
                            );
                          })() : (
                            <span className="no-data">-</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="seniority-text">{candidate.seniority || 'Not Specified'}</span>
                      </td>
                      <td>
                        <span className="location-text">{candidateProfile.location || '-'}</span>
                      </td>
                      <td>
                        <div className="status-badge" style={{ color: parseStatus.color }}>
                          <span className="status-icon">{parseStatus.icon}</span>
                          <span>{parseStatus.label}</span>
                        </div>
                      </td>
                      <td>
                        <span className="embedding-status" style={{ color: embeddingStatus.color }}>
                          {embeddingStatus.label}
                        </span>
                      </td>
                      <td>
                        <span className="synced-time">{formatTimeAgo(candidate.synced_time)}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="juliet-pagination">
            <div className="pagination-info">
              Showing {((currentPage - 1) * 10) + 1}-{Math.min(currentPage * 10, totalCount)} of {totalCount} candidates
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                ←
              </button>
              <div className="pagination-numbers">
                {(() => {
                  const getPageNumbers = (current, total) => {
                    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
                    const rangeStart = Math.max(2, current - 2);
                    const rangeEnd = Math.min(total - 1, current + 2);
                    const pages = [1];
                    if (rangeStart > 2) pages.push('...');
                    for (let p = rangeStart; p <= rangeEnd; p++) pages.push(p);
                    if (rangeEnd < total - 1) pages.push('...');
                    pages.push(total);
                    return pages;
                  };
                  return getPageNumbers(currentPage, totalPages).map((page, idx) =>
                    page === '...'
                      ? <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>
                      : <button
                          key={page}
                          className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                  );
                })()}
              </div>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Overflow tooltip — rendered at viewport level to escape overflow:auto clipping */}
      {overflowTooltip && (
        <div
          className="overflow-tooltip-fixed"
          style={{ left: overflowTooltip.x, top: overflowTooltip.y }}
          onMouseEnter={keepTooltip}
          onMouseLeave={hideTooltip}
        >
          <div className="overflow-tooltip-fixed-header">
            {overflowTooltip.items.length} more practice area{overflowTooltip.items.length !== 1 ? 's' : ''}
          </div>
          <div className="overflow-tooltip-fixed-body">
            {overflowTooltip.items.map((pa, i) => (
              <span
                key={i}
                className={`practice-area-tag${
                  practiceAreaFilter !== 'all' && pa.toLowerCase() === practiceAreaFilter.toLowerCase()
                    ? ' matched'
                    : ''
                }`}
              >
                {pa}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Candidate Profile Modal */}
      {showModal && (
        <CandidateProfileModal
          candidate={selectedCandidate}
          candidateId={selectedCandidateId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

export default Juliet;
